import { GetMeusPaineisGerenciais, type ReadPainelGerencialMeuDto } from './painel-gerencial.service';
import Links from '@/shared/constants/navegacao';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Loading from '@/shared/components/Carregando';

type LinhaItem = {
  key: string;
  nome: string;
  url: string;
};

function itensDoPainel(painel: ReadPainelGerencialMeuDto): LinhaItem[] {
  const minis = [...(painel.minis ?? [])].sort((a, b) => a.ordem - b.ordem || a.id - b.id);
  if (minis.length > 0) {
    return minis.map((mini) => ({
      key: `mini-${mini.id}`,
      nome: mini.nome,
      url: mini.url,
    }));
  }
  return [
    {
      key: `painel-${painel.id}`,
      nome: painel.nome,
      url: painel.url,
    },
  ];
}

function abrirUrl(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function CardAreaItens({
  area,
  itens,
}: {
  area: string;
  itens: LinhaItem[];
}) {
  return (
    <div className="card mb-3">
      <div className="card-header">{area}</div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <tbody>
              {itens.map((item) => (
                <tr
                  key={item.key}
                  role="link"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  title={item.url}
                  onClick={() => abrirUrl(item.url)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      abrirUrl(item.url);
                    }
                  }}
                >
                  <td>{item.nome}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function agruparPorArea(paineis: ReadPainelGerencialMeuDto[]) {
  const mapa = new Map<string, LinhaItem[]>();
  for (const painel of paineis) {
    const area = painel.areaResponsavel.trim() || 'Outros';
    const itens = mapa.get(area) ?? [];
    itens.push(...itensDoPainel(painel));
    mapa.set(area, itens);
  }
  return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b, 'pt-BR'));
}

export default function PaineisGerenciaisPage() {
  const params = useParams();
  const painelId = params.id ? Number(params.id) : null;

  const [loading, setLoading] = useState(true);
  const [paineis, setPaineis] = useState<ReadPainelGerencialMeuDto[]>([]);

  useEffect(() => {
    GetMeusPaineisGerenciais()
      .then((lista) => setPaineis(lista))
      .catch((err) => toast.error(String(err)))
      .finally(() => setLoading(false));
  }, []);

  const painelSelecionado = useMemo(() => {
    if (painelId == null || !Number.isFinite(painelId)) return null;
    return paineis.find((p) => p.id === painelId) ?? null;
  }, [paineis, painelId]);

  const gruposPorArea = useMemo(() => agruparPorArea(paineis), [paineis]);

  if (loading) return <Loading />;

  if (painelId != null) {
    if (!painelSelecionado) {
      return (
        <div className="row">
          <div className="col-12">
            <h3>Painéis Gerenciais</h3>
            <hr className="pb-4" />
            <p className="text-muted">Painel não encontrado ou sem acesso.</p>
            <Link to={Links.PaineisGerenciais_Index}>Voltar</Link>
          </div>
        </div>
      );
    }

    return (
      <div className="row">
        <div className="col-12">
          <h3>{painelSelecionado.nome}</h3>
          {painelSelecionado.descricao ? (
            <p className="text-muted small">{painelSelecionado.descricao}</p>
          ) : null}
          <hr className="pb-4" />
          <CardAreaItens
            area={painelSelecionado.areaResponsavel.trim() || 'Outros'}
            itens={itensDoPainel(painelSelecionado)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <h3>Painéis Gerenciais</h3>
        <hr className="pb-4" />
      </div>

      {paineis.length === 0 ? (
        <p className="text-muted">Nenhum painel liberado para você no momento.</p>
      ) : (
        <div className="col-12">
          {gruposPorArea.map(([area, itens]) => (
            <CardAreaItens key={area} area={area} itens={itens} />
          ))}
        </div>
      )}
    </div>
  );
}
